import { useEffect } from 'react';
import { stanNotify } from '@/lib/stanNotify';
import { INTEGRATION_STATUS, LOCAL_STORAGE_KEYS } from '@/lib/constants';
import type { IntegrationsResponse } from '@/types';

const FORMATTED_INTEGRATION_NAMES: Record<string, string> = {
    'google-calendar': 'Google Calendar',
    zoom: 'Zoom',
    instagram: 'Instagram',
};

/**
 * Hook to check for successful OAuth integration when user returns from OAuth flow.
 * Shows success notification and cleans up localStorage tracking.
 */
export function useOAuthIntegrationCallback(userIntegrations: IntegrationsResponse | undefined) {
    useEffect(() => {
        if (!userIntegrations) return;

        try {
            const integrationInProgress = JSON.parse(
                localStorage.getItem(LOCAL_STORAGE_KEYS.INTEGRATION_IN_PROGRESS) || '{}'
            ) as Record<string, boolean>;

            if (!integrationInProgress || Object.keys(integrationInProgress).length === 0) {
                return;
            }

            // Check each integration in progress
            Object.keys(integrationInProgress).forEach(integrationType => {
                const integration = Object.values(userIntegrations).find(int => int.type === integrationType);

                // If integration is now connected, show success toast
                if (integration?.status === INTEGRATION_STATUS.ACTIVE) {
                    const integrationName = FORMATTED_INTEGRATION_NAMES[integrationType] || integrationType;

                    stanNotify({
                        type: 'success',
                        title: `Successfully integrated ${integrationName}!`,
                    });

                    // Remove this integration from progress tracking
                    delete integrationInProgress[integrationType];
                }
            });

            // Update localStorage
            if (Object.keys(integrationInProgress).length === 0) {
                localStorage.removeItem(LOCAL_STORAGE_KEYS.INTEGRATION_IN_PROGRESS);
            } else {
                localStorage.setItem(LOCAL_STORAGE_KEYS.INTEGRATION_IN_PROGRESS, JSON.stringify(integrationInProgress));
            }
        } catch (error) {
            console.error('Error checking integration progress:', error);
        }
    }, [userIntegrations]);
}
